(() => {
  // International Morse letters, numbers and punctuation, plus common Latin extensions.
  const MORSE = Object.freeze({
    A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.',
    G: '--.', H: '....', I: '..', J: '.---', K: '-.-', L: '.-..',
    M: '--', N: '-.', O: '---', P: '.--.', Q: '--.-', R: '.-.',
    S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
    Y: '-.--', Z: '--..',
    0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-',
    5: '.....', 6: '-....', 7: '--...', 8: '---..', 9: '----.',
    '.': '.-.-.-', ',': '--..--', ':': '---...', ';': '-.-.-.',
    '?': '..--..', '!': '-.-.--', "'": '.----.', '"': '.-..-.',
    '-': '-....-', '/': '-..-.', '(': '-.--.', ')': '-.--.-',
    '=': '-...-', '+': '.-.-.', '&': '.-...', '_': '..--.-',
    '$': '...-..-', '@': '.--.-.', '×': '-..-',
    'À': '.--.-', 'Á': '.--.-', 'Ä': '.-.-', 'Å': '.--.-',
    'Ą': '.-.-', 'Æ': '.-.-', 'Ç': '-.-..', 'Ć': '-.-..', 'Ĉ': '-.-..',
    'Đ': '..-..', 'Ð': '..--.', 'É': '..-..', 'È': '.-..-', 'Ę': '..-..',
    'Ĝ': '--.-.', 'Ĥ': '----', 'Ĵ': '.---.', 'Ł': '.-..-',
    'Ñ': '--.--', 'Ń': '--.--', 'Ó': '---.', 'Ö': '---.', 'Ø': '---.',
    'Ś': '...-...', 'Ŝ': '...-.', 'Š': '----', 'Þ': '.--..',
    'Ü': '..--', 'Ŭ': '..--', 'Ź': '--..-.', 'Ż': '--..-'
  });

  function normalizeText(text) {
    return text.normalize('NFC').replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
      .replace(/[‐‑–—−]/g, '-').replace(/\s+/gu, ' ').trim();
  }

  function translate(text) {
    const normalized = normalizeText(text);
    const unsupported = [...new Set([...normalized].filter(character =>
      character !== ' ' && !Object.hasOwn(MORSE, character.toUpperCase())
    ))];
    const words = unsupported.length || !normalized ? [] : normalized.split(' ').map(word =>
      [...word].map(character => ({ character, code: MORSE[character.toUpperCase()] }))
    );
    return { normalized, unsupported, words };
  }

  class MorseNecklaceBuilder extends HTMLElement {
    connectedCallback() {
      // Wait for all child markup when the element connects during parsing.
      if (this.controller) return;
      this.form = this.querySelector('form');
      if (!this.querySelector('[data-morse-submit]')) {
        this.observer = new MutationObserver(() => {
          if (!this.querySelector('[data-morse-submit]')) return;
          this.observer.disconnect();
          this.connectedCallback();
        });
        this.observer.observe(this, { childList: true, subtree: true });
        return;
      }
      this.input = this.querySelector('input');
      this.error = this.querySelector('[data-morse-error]');
      this.strand = this.querySelector('[data-morse-strand]');
      this.scroll = this.querySelector('[data-morse-scroll]');
      this.description = this.querySelector('[data-morse-description]');
      this.button = this.querySelector('[data-morse-submit]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input.addEventListener('input', () => this.update(), options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.update();
        if (!this.requestUrl) return;
        window.location.assign(this.requestUrl);
      }, options);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }

    update() {
      const result = translate(this.input.value);
      const phone = (this.dataset.whatsappNumber || '').replace(/[\s()+.-]/g, '');
      const validPhone = /^[1-9]\d{6,14}$/.test(phone);
      const validText = result.normalized.length > 0 && result.unsupported.length === 0;
      this.requestUrl = null;
      let error = '';
      if (result.unsupported.length) {
        error = this.dataset.errorCharacters.replace('__MORSE_CHARACTERS__', () => result.unsupported.join(' '));
      } else if (!validPhone) {
        error = this.dataset.errorUnavailable;
      }
      this.error.textContent = error;
      this.error.hidden = !error;
      this.input.setAttribute('aria-invalid', String(result.unsupported.length > 0));
      this.input.setCustomValidity(result.unsupported.length ? error : '');
      this.button.disabled = result.unsupported.length > 0 || !validPhone;
      this.scroll.hidden = !validText;
      this.strand.replaceChildren();
      const code = result.words.map(word => word.map(letter => letter.code).join(' ')).join(' / ');
      if (!this.button.disabled) {
        let message = this.dataset.requestMessage;
        if (validText) {
          message += `\n\n${this.dataset.requestTextLabel}: ${result.normalized}\n${this.dataset.requestCodeLabel}: ${code}`;
        }
        this.requestUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      }
      if (!validText) {
        this.description.textContent = '';
        return;
      }
      // Keep every mark equally spaced, with the complete design centered.
      // Scale the single strand to fit on small screens without clipping it.
      const marks = result.words.flat().map(letter => letter.code).join('');
      const svgNamespace = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNamespace, 'svg');
      let x = 0;
      for (const mark of marks) {
        const shape = document.createElementNS(svgNamespace, mark === '.' ? 'circle' : 'rect');
        const width = mark === '.' ? 8 : 24;
        if (mark === '.') {
          shape.setAttribute('cx', x + 4);
          shape.setAttribute('cy', 5);
          shape.setAttribute('r', 4);
        } else {
          shape.setAttribute('x', x);
          shape.setAttribute('y', 1);
          shape.setAttribute('width', width);
          shape.setAttribute('height', 8);
          shape.setAttribute('rx', 1);
        }
        shape.setAttribute('fill', '#000');
        svg.append(shape);
        x += width + 8;
      }
      const width = x - 8;
      svg.setAttribute('viewBox', `0 0 ${width} 10`);
      svg.setAttribute('width', width);
      svg.setAttribute('height', 10);
      svg.style.width = `${width}px`;
      svg.setAttribute('focusable', 'false');
      this.strand.append(svg);
      this.description.textContent = this.dataset.codeDescription.replace(
        /__MORSE_TEXT__|__MORSE_CODE__/g,
        token => token === '__MORSE_TEXT__' ? result.normalized : code
      );
    }
  }

  if (!customElements.get('morse-necklace-builder')) {
    customElements.define('morse-necklace-builder', MorseNecklaceBuilder);
  }
})();
